import { useEffect, useRef, useState } from "react";

export type NewDocument = {
  name: string;
  type: string;
  size: string;
  expires: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  onUpload: (doc: NewDocument) => void;
};

const DOC_TYPES = [
  "Identity",
  "Payroll",
  "Compliance",
  "Certification",
  "Onboarding",
  "Other",
];

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function UploadDocumentModal({ open, onClose, onUpload }: Props) {
  const [docName, setDocName] = useState("");
  const [docType, setDocType] = useState(DOC_TYPES[2]);
  const [expiresNote, setExpiresNote] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) {
      setDocName("");
      setDocType(DOC_TYPES[2]);
      setExpiresNote("");
      setFile(null);
      setDragOver(false);
      setSubmitting(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const handleFile = (f: File | null) => {
    if (!f) return;
    setFile(f);
    if (!docName.trim()) {
      const base = f.name.replace(/\.[^.]+$/, "");
      setDocName(base);
    }
  };

  const canSubmit = !!file && docName.trim().length > 0 && !submitting;

  const handleSubmit = () => {
    if (!canSubmit || !file) return;
    setSubmitting(true);
    // Simulate a short upload delay so the UI feels real
    window.setTimeout(() => {
      onUpload({
        name: docName.trim(),
        type: docType,
        size: formatSize(file.size),
        expires: expiresNote.trim() || `Uploaded ${new Date().toLocaleDateString("en-US", { month: "short", year: "numeric" })}`,
      });
      setSubmitting(false);
      onClose();
    }, 500);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-ink-900/50 backdrop-blur-sm"
        onClick={onClose}
      ></div>
      <div className="relative bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col border border-ink-100">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-ink-100">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-md bg-brand-50 text-brand-700 flex items-center justify-center">
              <i className="ri-upload-cloud-2-line"></i>
            </div>
            <div>
              <h3 className="font-display text-base font-bold text-ink-900">
                Upload employee file
              </h3>
              <p className="text-[11px] text-ink-500 mt-0.5">
                Attach a document to this crew member's record
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-cream-100 text-ink-500 cursor-pointer"
            aria-label="Close"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4 overflow-y-auto">
          {/* Dropzone */}
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              const f = e.dataTransfer.files?.[0];
              if (f) handleFile(f);
            }}
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
              dragOver
                ? "border-brand-500 bg-brand-50/50"
                : "border-ink-200 hover:border-ink-300 hover:bg-cream-50"
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
              onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
            />
            {file ? (
              <div className="flex items-center justify-center gap-3">
                <div className="w-10 h-10 rounded-md bg-brand-50 text-brand-700 flex items-center justify-center">
                  <i className="ri-file-line text-lg"></i>
                </div>
                <div className="text-left">
                  <div className="text-sm font-bold text-ink-900 truncate max-w-[260px]">
                    {file.name}
                  </div>
                  <div className="text-[11px] text-ink-500 mt-0.5">
                    {formatSize(file.size)} · click to replace
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="w-10 h-10 rounded-full bg-cream-100 text-ink-500 flex items-center justify-center mx-auto mb-2">
                  <i className="ri-upload-cloud-2-line text-lg"></i>
                </div>
                <div className="text-sm font-bold text-ink-900">
                  Drop file here or click to browse
                </div>
                <div className="text-[11px] text-ink-500 mt-1">
                  PDF, DOC, PNG, JPG · up to 10 MB
                </div>
              </>
            )}
          </div>

          {/* Doc name */}
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-wider text-ink-500 mb-1.5">
              Document name
            </label>
            <input
              type="text"
              value={docName}
              onChange={(e) => setDocName(e.target.value)}
              placeholder="e.g. CDL License renewal"
              className="w-full px-3 py-2 text-sm border border-ink-200 rounded-md focus:outline-none focus:border-brand-500 bg-white"
            />
          </div>

          {/* Type */}
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-wider text-ink-500 mb-1.5">
              Category
            </label>
            <div className="relative">
              <select
                value={docType}
                onChange={(e) => setDocType(e.target.value)}
                className="w-full px-3 py-2 pr-8 text-sm border border-ink-200 rounded-md focus:outline-none focus:border-brand-500 bg-white appearance-none cursor-pointer"
              >
                {DOC_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
              <i className="ri-arrow-down-s-line absolute right-2.5 top-1/2 -translate-y-1/2 text-ink-500 pointer-events-none"></i>
            </div>
          </div>

          {/* Expires / note */}
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-wider text-ink-500 mb-1.5">
              Note <span className="text-ink-400 font-normal normal-case tracking-normal">(optional)</span>
            </label>
            <input
              type="text"
              value={expiresNote}
              onChange={(e) => setExpiresNote(e.target.value)}
              placeholder="e.g. Expires Mar 2029"
              className="w-full px-3 py-2 text-sm border border-ink-200 rounded-md focus:outline-none focus:border-brand-500 bg-white"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3.5 border-t border-ink-100 bg-cream-50">
          <button
            onClick={onClose}
            className="px-3.5 py-2 rounded-md text-xs font-bold text-ink-700 hover:bg-cream-100 cursor-pointer whitespace-nowrap"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md text-xs font-bold whitespace-nowrap ${
              canSubmit
                ? "bg-ink-900 hover:bg-ink-800 text-white cursor-pointer"
                : "bg-ink-200 text-ink-400 cursor-not-allowed"
            }`}
          >
            {submitting ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i>
                Uploading…
              </>
            ) : (
              <>
                <i className="ri-upload-2-line"></i>
                Upload file
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}